import React from "react";

import Dialog from "@mui/material/Dialog";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";

import styles from "./FartherAgreementsModal.module.css";

interface AgreementSectionProps {
  heading: string;
  summary: string;
}

const AgreementSection = ({
  heading,
  summary,
}: AgreementSectionProps): JSX.Element => {
  return (
    <div className={styles.agreement_div}>
      <p className={styles.agreement_heading}>{heading}</p>

      <p className={styles.agreement_summary}>{summary}</p>
    </div>
  );
};

interface FartherAgreementsModalProps {
  isModalOpen: boolean;
  isLoading: boolean;
  isErrored: boolean;
  onAccept: () => void;
  onClose: () => void;
}

const FartherAgreementsModal = ({
  isModalOpen,
  isLoading,
  isErrored,
  onAccept,
  onClose,
}: FartherAgreementsModalProps): JSX.Element => {
  const { isImpersonationMode } = useApplicationMode();

  if (isErrored) {
    return (
      <Dialog
        open={isModalOpen}
        onClose={onClose}
        PaperProps={{ elevation: 0, className: styles.paper }}
      >
        <div className={styles.modal_content}>
          <div>
            <p className={styles.title}>E-sign Farther Agreements</p>

            <p className={styles.description}>Error loading agreements</p>
          </div>
        </div>

        <div className={styles.modal_footer} />
      </Dialog>
    );
  }

  if (isLoading) {
    return (
      <Dialog
        open={isModalOpen}
        onClose={onClose}
        PaperProps={{ elevation: 0, className: styles.paper }}
      >
        <div className={styles.modal_content}>
          <div>
            <p className={styles.title}>E-sign Farther Agreements</p>

            <p className={styles.description}>
              Here is an overview of the Farther Agreements. Review the
              agreements and e-sign.
            </p>

            <div style={{ height: 20, width: "100%" }} />

            <Skeleton
              width={478 + 40} // 20 padding
              height={78 + 40} // 20 padding
              style={{ transform: "none" }}
            />

            <div style={{ height: 20, width: "100%" }} />

            <Skeleton
              width={478 + 40} // 20 padding
              height={78 + 40} // 20 padding
              style={{ transform: "none" }}
            />

            <div style={{ height: 20, width: "100%" }} />

            <Skeleton
              width={478 + 40} // 20 padding
              height={78 + 40} // 20 padding
              style={{ transform: "none" }}
            />
          </div>
        </div>

        <div className={styles.modal_footer}>
          <Skeleton
            width={103 + 32} // 16 padding
            height={25 + 12} // 6 padding
            style={{ transform: "none" }}
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isModalOpen}
      onClose={onClose}
      PaperProps={{ elevation: 0, className: styles.paper }}
    >
      <>
        <div className={styles.modal_content}>
          <div>
            <p className={styles.title}>E-sign Farther Agreements</p>

            <p className={styles.description}>
              Here is an overview of the Farther Agreements. Review the
              agreements and e-sign.
            </p>

            <AgreementSection
              heading="Farther IAA"
              summary="This document details the terms and conditions of your
                    relationship with Farther as your Investment Advisor."
            />

            <AgreementSection
              heading="Farther ADV"
              summary="This document provides information about the qualifications and
                    business practices of Farther Finance Advisors, LLC."
            />

            <AgreementSection
              heading="Farther CRS"
              summary="This document details our investment advisory and financial planning services."
            />
          </div>
        </div>

        <div className={styles.modal_footer}>
          <Button
            style={{
              flexShrink: 0,
              cursor: isImpersonationMode ? "not-allowed" : "pointer",
            }}
            disabled={isImpersonationMode}
            variant="contained"
            buttonType="primary"
            text="Continue to Sign"
            onClick={onAccept}
          />
        </div>
      </>
    </Dialog>
  );
};

export default FartherAgreementsModal;
